import { modals } from '@mantine/modals'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { BENESTREAM_ORG_ID } from 'src/common/config'
import { cleanStringToBeUrlReadable } from 'src/default/helpers/clean-string'
import { useAppDispatch, useAppSelector } from 'src/v2/store/hooks'

import { showCrossOrganizationNavigationModal } from '../components/CrossOrganizationNavigationModal/CrossOrganizationNavigationModal.component'
import {
  selectIsOrganizationSwitching,
  selectPendingOrganizationSwitchEvent,
} from '../selectors/organizationSwitch.selectors'
import { checkIfNeedsOrganizationSwitch } from '../utils/crossOrganizationNavigation.utils'
import { getDefaultMembershipId } from '../utils/organizationMembership.utils'
import {
  getLastValidOrganizationLocation,
  rememberLastValidOrganizationLocation,
} from '../utils/organizationNavigationSession.utils'
import { useChangeOrganizationProcess } from './useChangeOrganizationProcess.hook'

import { setGlobalIsLoading } from '@v2/domains/ui/store/ui.store'
import {
  selectCurrentOrganization,
  selectOrganizations,
} from '@v2/domains/user/selectors/user.selectors'

export const useCrossOrganizationNavigationGuard = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const currentOrganization = useAppSelector(selectCurrentOrganization)
  const isOrganizationSwitching = useAppSelector(selectIsOrganizationSwitching)
  const pendingEvent = useAppSelector(selectPendingOrganizationSwitchEvent)
  const userOrganizations = useAppSelector(selectOrganizations)
  const { handleChangeOrganization, isChanging } =
    useChangeOrganizationProcess()

  const isChangingOrgRef = useRef(false)
  const isModalOpenRef = useRef(false)
  const isRestoringRef = useRef(false)

  const cleanupGuardState = useCallback(() => {
    isChangingOrgRef.current = false
    isRestoringRef.current = false
  }, [])
  const lastCheckedOrgRef = useRef<string | null>(null)

  const organizationCheck = useMemo(() => {
    if (!currentOrganization?.id) {
      return null
    }

    return checkIfNeedsOrganizationSwitch(
      location.pathname,
      currentOrganization,
      userOrganizations || [],
    )
  }, [currentOrganization, location.pathname, userOrganizations])

  const shouldBlockRendering =
    Boolean(pendingEvent) ||
    Boolean(
      organizationCheck &&
        (organizationCheck.isInvalidOrganization ||
          organizationCheck.needsSwitch ||
          (organizationCheck.urlOrganizationName &&
            !organizationCheck.hasAccess &&
            !organizationCheck.isPublicRoute)),
    )

  const restorePreviousLocation = useCallback(() => {
    const previousLocation =
      getLastValidOrganizationLocation(currentOrganization)
    const currentLocation = `${location.pathname}${location.search}${location.hash}`
    if (previousLocation && previousLocation !== currentLocation) {
      navigate(previousLocation, { replace: true })
      return
    }

    if (currentOrganization?.name) {
      const fallbackPath = `/${cleanStringToBeUrlReadable(
        currentOrganization.name,
      )}/leads`
      navigate(fallbackPath, { replace: true })
      return
    }

    navigate('/', { replace: true })
  }, [
    currentOrganization,
    navigate,
    location.pathname,
    location.search,
    location.hash,
  ])

  useEffect(() => {
    if (
      pendingEvent ||
      isOrganizationSwitching ||
      isRestoringRef.current ||
      !organizationCheck ||
      organizationCheck.isPublicRoute ||
      organizationCheck.isInvalidOrganization ||
      organizationCheck.needsSwitch ||
      !organizationCheck.hasAccess
    ) {
      return
    }

    rememberLastValidOrganizationLocation(
      currentOrganization,
      `${location.pathname}${location.search}${location.hash}`,
    )
  }, [
    currentOrganization,
    isOrganizationSwitching,
    location.pathname,
    location.search,
    location.hash,
    organizationCheck,
    pendingEvent,
  ])

  useEffect(() => {
    if (pendingEvent) {
      if (isModalOpenRef.current) {
        modals.closeAll()
        isModalOpenRef.current = false
        dispatch(setGlobalIsLoading(false))
      }
      return
    }

    if (
      !currentOrganization?.id ||
      isChangingOrgRef.current ||
      isOrganizationSwitching
    ) {
      return
    }

    if (isRestoringRef.current) {
      return
    }

    if (isModalOpenRef.current) {
      return
    }

    const currentPath = location.pathname
    const checkResult = organizationCheck

    if (!checkResult) {
      return
    }

    if (checkResult.isInvalidOrganization) {
      restorePreviousLocation()
      return
    }

    if (
      checkResult.urlOrganizationName &&
      !checkResult.hasAccess &&
      !checkResult.isPublicRoute
    ) {
      dispatch(setGlobalIsLoading(true))

      isModalOpenRef.current = true
      showCrossOrganizationNavigationModal({
        targetOrganization: undefined,
        targetUrl: currentPath,
        hasAccess: false,
        isLoading: false,
        onConfirm: () => {
          isRestoringRef.current = true
          restorePreviousLocation()

          modals.closeAll()
          isModalOpenRef.current = false
          dispatch(setGlobalIsLoading(false))

          isRestoringRef.current = false
          cleanupGuardState()
        },
        onCancel: () => {
          isRestoringRef.current = true
          restorePreviousLocation()

          modals.closeAll()
          isModalOpenRef.current = false
          dispatch(setGlobalIsLoading(false))

          isRestoringRef.current = false
          cleanupGuardState()
        },
      })
      return
    }

    if (!checkResult.needsSwitch) {
      lastCheckedOrgRef.current = currentOrganization.id
      return
    }

    if (lastCheckedOrgRef.current === checkResult.targetOrganization?.id) {
      return
    }

    const handleChangeOrg = async () => {
      if (!checkResult.targetOrganization || !checkResult.targetPath) {
        modals.closeAll()
        isModalOpenRef.current = false
        dispatch(setGlobalIsLoading(false))
        restorePreviousLocation()
        return
      }

      const organizationId = checkResult.targetOrganization.id
      const membershipId = getDefaultMembershipId(
        checkResult.targetOrganization,
      )
      const targetPath = checkResult.targetPath

      try {
        isChangingOrgRef.current = true

        await handleChangeOrganization({
          organizationId,
          membershipId,
          targetPath,
          source: 'guard',
        })

        modals.closeAll()
        isModalOpenRef.current = false
        lastCheckedOrgRef.current = organizationId
      } catch (error) {
        dispatch(setGlobalIsLoading(false))
        restorePreviousLocation()
        cleanupGuardState()
      }
    }

    if (
      currentOrganization.id === BENESTREAM_ORG_ID &&
      checkResult.targetOrganization?.id !== BENESTREAM_ORG_ID
    ) {
      handleChangeOrg()

      return
    }

    dispatch(setGlobalIsLoading(true))

    lastCheckedOrgRef.current = checkResult.targetOrganization?.id || null
    isModalOpenRef.current = true

    showCrossOrganizationNavigationModal({
      targetOrganization: checkResult.targetOrganization,
      targetUrl: checkResult.targetPath || currentPath,
      hasAccess: checkResult.hasAccess,
      isLoading: isChanging,
      onConfirm: handleChangeOrg,
      onCancel: () => {
        isRestoringRef.current = true
        restorePreviousLocation()

        dispatch(setGlobalIsLoading(false))
        modals.closeAll()
        isModalOpenRef.current = false
        lastCheckedOrgRef.current = currentOrganization.id

        isRestoringRef.current = false
        cleanupGuardState()
      },
    })
  }, [
    location.pathname,
    currentOrganization,
    isOrganizationSwitching,
    organizationCheck,
    handleChangeOrganization,
    isChanging,
    dispatch,
    cleanupGuardState,
    restorePreviousLocation,
    pendingEvent,
  ])

  useEffect(() => {
    return () => {
      if (isModalOpenRef.current) {
        modals.closeAll()
        isModalOpenRef.current = false
        dispatch(setGlobalIsLoading(false))
        isRestoringRef.current = false
        cleanupGuardState()
      }
    }
  }, [dispatch, cleanupGuardState])

  return {
    shouldBlockRendering,
  }
}
